/** The placement check — a few questions that decide where to start.
 *
 * The client's ask: onboarding should judge the level rather than asking for
 * it, "level A1 or B1, or even Basic, then we need to guide."
 *
 * Fifteen questions, easiest first, drawn from the practice bank that
 * already exists. It reuses the same answer widgets the practice screens
 * use, so a learner meets nothing unfamiliar here, and it costs no AI
 * credits because everything in it is objectively marked.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "../../api/placement";
import { Badge, Button, Card, EmptyState, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { FORMATS } from "../practice/ExamItemPlayerPage";

type Answers = Record<string, Record<string, unknown>>;

export function PlacementPage() {
  const t = useT();
  const [set, setSet] = useState<api.PlacementSet | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [result, setResult] = useState<api.PlacementResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .placementQuestions()
      .then((s) => {
        if (!cancelled) setSet(s);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <Card>
        <p role="alert" className="text-sm text-danger">{t("common.error.generic")}</p>
      </Card>
    );
  }
  if (!set) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  if (result) return <Outcome result={result} />;

  if (set.questions.length === 0) {
    return (
      <Card>
        <EmptyState message={t("placement.noQuestions")} />
      </Card>
    );
  }

  const question = set.questions[index];
  const Format = FORMATS[question.item_type];
  const last = index === set.questions.length - 1;

  const setAnswer = (qid: string, value: unknown) =>
    setAnswers((all) => ({
      ...all,
      [question.id]: { ...(all[question.id] ?? {}), [qid]: value },
    }));

  const submit = async () => {
    setBusy(true);
    try {
      setResult(await api.submitPlacement(answers));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="text-3xl font-extrabold">{t("placement.title")}</h1>
        <p className="mt-1 text-muted">{t("placement.subtitle")}</p>
      </div>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase text-muted">
            {t("placement.progress", {
              current: index + 1, total: set.questions.length,
            })}
          </span>
          {/* The level is deliberately NOT shown: knowing a question is "B2"
              changes how hard a learner tries at it. */}
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-canvas">
          <div
            className="h-full bg-brand transition-all"
            style={{ width: `${((index + 1) / set.questions.length) * 100}%` }}
          />
        </div>

        <div className="mt-4">
          {Format ? (
            <Format
              payload={question.payload as never}
              answers={(answers[question.id] ?? {}) as never}
              setAnswer={setAnswer as never}
            />
          ) : (
            <EmptyState message={t("placement.skipQuestion")} />
          )}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
          >
            {t("placement.back")}
          </Button>
          {last ? (
            <Button onClick={() => void submit()} loading={busy}>
              {t("placement.finish")}
            </Button>
          ) : (
            <Button onClick={() => setIndex((i) => i + 1)}>
              {t("placement.next")}
            </Button>
          )}
        </div>
        <p className="mt-3 text-xs text-muted">{t("placement.dontKnowHint")}</p>
      </Card>
    </div>
  );
}

function Outcome({ result }: { result: api.PlacementResult }) {
  const t = useT();
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Card>
        <p className="text-xs font-semibold uppercase text-muted">
          {t("placement.startHere")}
        </p>
        <p className="mt-1 text-5xl font-extrabold text-ink">{result.level}</p>
        <p className="mt-3 text-sm text-ink">
          {t(
            result.reason === "cleared_everything"
              ? "placement.reasonTop"
              : result.reason === "start_at_the_beginning"
                ? "placement.reasonBeginner"
                : "placement.reasonNext",
          )}
        </p>

        {/* Show the working. A level handed down without a reason is not
            believed, and a learner who does not believe it ignores it. */}
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(result.per_level).map(([level, row]) => (
            <Badge key={level} tone={row.correct >= row.total * 0.6 ? "brand" : "neutral"}>
              {level}: {row.correct}/{row.total}
            </Badge>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/learn">
            <Button>{t("placement.startLearning")}</Button>
          </Link>
          <Link to="/practice">
            <Button variant="secondary">{t("placement.goPractice")}</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
